/**
 * Create Ticket Page
 * @description 티켓 생성 페이지 - Workflow 서비스 유형 → 요청 유형 → 상세 폼 (시스템 선택은 선택적)
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from '@shared/router';
import {
  Button, Card, Input, Select, SelectContent, SelectItem, SelectTrigger,
  SelectValue, Textarea
} from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import { getTenantConfig } from '@/services/tenantConfigApi';
import { createTicket } from '@/services/ticketApi';
import type { System, RequestType, WorkflowServiceType, FormField } from '@/services/tenantConfigApi';
import type { TicketType, Priority } from '@/types/ticket.types';

interface CreateTicketPageProps {
  ticketType: TicketType;
}

function isEnabledInDomain(
  enabledInDomains: string[] | undefined,
  domain: TicketType
): boolean {
  if (!enabledInDomains?.length) return true;
  return enabledInDomains.includes(domain);
}

export const CreateTicketPage: React.FC<CreateTicketPageProps> = ({ ticketType }) => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [systems, setSystems] = useState<System[]>([]);
  const [serviceTypes, setServiceTypes] = useState<WorkflowServiceType[]>([]);
  const [requestTypesForDomain, setRequestTypesForDomain] = useState<RequestType[]>([]);

  const [selectedSystem, setSelectedSystem] = useState<string>('');
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<string>('');
  const [selectedRequestType, setSelectedRequestType] = useState<RequestType | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [customFields, setCustomFields] = useState<Record<string, any>>({});

  const hasSystemStep = systems.length > 0;
  const totalSteps = hasSystemStep ? 4 : 3;

  useEffect(() => {
    loadConfig();
  }, [user, ticketType]);

  const loadConfig = async () => {
    if (!user || user.userType !== 'tenant-user') return;
    try {
      setIsLoading(true);
      const config = await getTenantConfig(user.tenantId);
      const rawSystems = config.catalogs?.systems ?? [];
      const rawServiceTypes = config.workflows?.serviceTypes ?? [];
      const rawRequestTypes = config.workflows?.requestTypes ?? [];
      setSystems(Array.isArray(rawSystems) ? rawSystems : []);

      const rtList = Array.isArray(rawRequestTypes) ? rawRequestTypes : [];
      const filtered = rtList.filter(
        (rt) =>
          rt.domain === ticketType &&
          isEnabledInDomain(rt.enabledInDomains, ticketType)
      );
      setRequestTypesForDomain(filtered);

      const typeIdsWithRequests = [...new Set(filtered.map((r) => r.workflowServiceTypeId).filter(Boolean))] as string[];
      const stList = Array.isArray(rawServiceTypes) ? rawServiceTypes : [];
      const visibleTypes = stList.filter(
        (st) =>
          isEnabledInDomain(st.enabledInDomains, ticketType) &&
          (typeIdsWithRequests.length === 0 || typeIdsWithRequests.includes(st.serviceTypeId))
      );
      setServiceTypes(visibleTypes);
    } catch (err) {
      setError(err instanceof Error ? err.message : '설정을 불러올 수 없습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedSystemObj = useMemo(
    () => systems.find((s) => s.systemId === selectedSystem),
    [systems, selectedSystem]
  );

  const visibleRequestTypes = useMemo(() => {
    let list = requestTypesForDomain;
    if (selectedSystemObj) {
      list = list.filter((rt) => rt.category === selectedSystemObj.category);
    }
    if (selectedServiceTypeId) {
      list = list.filter((rt) => rt.workflowServiceTypeId === selectedServiceTypeId);
    }
    return list;
  }, [requestTypesForDomain, selectedSystemObj, selectedServiceTypeId]);

  const handleSystemSelect = (systemId: string) => {
    setSelectedSystem(systemId);
  };

  const handleServiceTypeSelect = (serviceTypeId: string) => {
    setSelectedServiceTypeId(serviceTypeId);
    setSelectedRequestType(null);
  };

  const handleRequestTypeSelect = (requestTypeId: string) => {
    const rt = visibleRequestTypes.find((r) => r.requestTypeId === requestTypeId);
    setSelectedRequestType(rt ?? null);
  };

  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFields((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async () => {
    if (!user || user.userType !== 'tenant-user' || !selectedRequestType) return;
    try {
      setIsLoading(true);
      setError(null);
      const scope = selectedSystem || selectedRequestType.category || 'portal';
      const ticket = await createTicket(user.tenantId, {
        ticketType,
        requestTypeId: selectedRequestType.requestTypeId,
        scope,
        title,
        description,
        priority,
        customFields,
      });
      navigate({ to: `/it-service/tickets/${ticket.ticketId}` });
    } catch (err) {
      setError(err instanceof Error ? err.message : '티켓 생성에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const stepLabels = hasSystemStep
    ? ['시스템 선택', 'Workflow 서비스 유형', '요청 유형 선택', '상세 정보']
    : ['Workflow 서비스 유형', '요청 유형 선택', '상세 정보'];

  const canProceedStep1 = hasSystemStep ? selectedSystem !== '' : true;
  const canProceedStep2 = selectedRequestType !== null;
  const canSubmit = title.trim() !== '' && description.trim() !== '';

  const ticketTypeLabels: Record<TicketType, string> = {
    servicerequest: '서비스 요청',
    incident: '장애 보고',
    changerequest: '변경 요청',
  };

  if (user?.userType !== 'tenant-user') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Tenant 사용자만 접근 가능합니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-xl p-8 border border-blue-100 dark:border-blue-800/30">
        <div className="flex items-center gap-6">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/it-service' })}
            className="gap-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            취소
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              새 {ticketTypeLabels[ticketType]}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Step {step} / {totalSteps} - {stepLabels[step - 1]}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {stepLabels.map((_, i) => (
          <div key={i} className="flex items-center flex-1">
            <div
              className={`flex-1 h-3 rounded-full transition-all duration-300 ${
                i + 1 <= step ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
            {i < stepLabels.length - 1 && (
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center mx-2 text-sm font-bold transition-all duration-300 ${
                  i + 1 < step ? 'bg-green-500 text-white' : i + 1 === step ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                }`}
              >
                {i + 1 < step ? '✓' : i + 1}
              </div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <Card className="p-4 bg-danger/10 border-danger/20">
          <p className="text-sm text-danger">{error}</p>
        </Card>
      )}

      {/* Step 1a: 시스템 선택 (있을 때만) */}
      {hasSystemStep && step === 1 && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">시스템 선택</h2>
          <p className="text-sm text-muted-foreground mb-6">요청할 시스템을 선택하세요</p>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">로딩 중...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {systems.map((system) => (
                <Card
                  key={system.systemId}
                  className={`p-4 cursor-pointer transition-all ${
                    selectedSystem === system.systemId ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => handleSystemSelect(system.systemId)}
                >
                  <h3 className="font-semibold text-foreground mb-1">{system.name}</h3>
                  {system.description && <p className="text-sm text-muted-foreground">{system.description}</p>}
                  <p className="text-xs text-muted-foreground mt-2">{system.category}</p>
                </Card>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-6">
            <Button onClick={() => setStep(2)} disabled={!canProceedStep1} className="gap-2">
              다음 <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 1b or 2: Workflow 서비스 유형 */}
      {((!hasSystemStep && step === 1) || (hasSystemStep && step === 2)) && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Workflow 서비스 유형</h2>
          <p className="text-sm text-muted-foreground mb-6">유형을 선택하면 해당하는 요청 유형만 다음 단계에 표시됩니다.</p>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">로딩 중...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceTypes.map((st) => (
                <Card
                  key={st.serviceTypeId}
                  className={`p-4 cursor-pointer transition-all ${
                    selectedServiceTypeId === st.serviceTypeId ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => handleServiceTypeSelect(st.serviceTypeId)}
                >
                  <h3 className="font-semibold text-foreground mb-1">{st.displayName}</h3>
                  {st.description && <p className="text-sm text-muted-foreground">{st.description}</p>}
                </Card>
              ))}
              {serviceTypes.length === 0 && visibleRequestTypes.length > 0 && (
                <p className="text-sm text-muted-foreground col-span-full">등록된 서비스 유형이 없습니다. 다음 단계에서 요청 유형을 선택하세요.</p>
              )}
            </div>
          )}
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(hasSystemStep ? 1 : 1)} className="gap-2" disabled={!hasSystemStep}>
              <ArrowLeft className="w-4 h-4" /> 이전
            </Button>
            <Button
              onClick={() => setStep(hasSystemStep ? 3 : 2)}
              disabled={false}
              className="gap-2"
            >
              다음 <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2b or 3: 요청 유형 선택 */}
      {((!hasSystemStep && step === 2) || (hasSystemStep && step === 3)) && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">요청 유형 선택</h2>
          <p className="text-sm text-muted-foreground mb-6">요청 유형을 선택하세요</p>
          {visibleRequestTypes.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {selectedServiceTypeId ? '이 유형에 해당하는 요청 유형이 없습니다.' : '등록된 요청 유형이 없습니다. Workflow 설정에서 추가하세요.'}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleRequestTypes.map((rt) => (
                <Card
                  key={rt.requestTypeId}
                  className={`p-4 cursor-pointer transition-all ${
                    selectedRequestType?.requestTypeId === rt.requestTypeId ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => handleRequestTypeSelect(rt.requestTypeId)}
                >
                  <h3 className="font-semibold text-foreground mb-1">{rt.displayName}</h3>
                  {rt.description && <p className="text-sm text-muted-foreground">{rt.description}</p>}
                </Card>
              ))}
            </div>
          )}
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(hasSystemStep ? 2 : 1)} className="gap-2">
              <ArrowLeft className="w-4 h-4" /> 이전
            </Button>
            <Button onClick={() => setStep(hasSystemStep ? 4 : 3)} disabled={!canProceedStep2} className="gap-2">
              다음 <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3b or 4: 상세 정보 */}
      {((!hasSystemStep && step === 3) || (hasSystemStep && step === 4)) && selectedRequestType && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">상세 정보 입력</h2>
          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">제목 <span className="text-danger">*</span></label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="티켓 제목을 입력하세요" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">설명 <span className="text-danger">*</span></label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="상세 설명을 입력하세요" rows={6} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">우선순위</label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">낮음</SelectItem>
                  <SelectItem value="medium">보통</SelectItem>
                  <SelectItem value="high">높음</SelectItem>
                  <SelectItem value="critical">긴급</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(selectedRequestType.form?.fields ?? []).map((field) => (
              <div key={field.fieldId}>
                <label className="text-sm font-medium text-foreground mb-1 block">
                  {field.label}
                  {field.required && <span className="text-danger"> *</span>}
                </label>
                {renderFormField(field, customFields[field.fieldId], (value) => handleCustomFieldChange(field.fieldId, value))}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(hasSystemStep ? 3 : 2)} className="gap-2">
              <ArrowLeft className="w-4 h-4" /> 이전
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || isLoading} className="gap-2">
              <Save className="w-4 h-4" /> {isLoading ? '생성 중...' : '티켓 생성'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

function renderFormField(field: FormField, value: any, onChange: (value: any) => void) {
  switch (field.type) {
    case 'text':
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={4} />;
    case 'select':
      return (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder={field.placeholder || '선택하세요'} /></SelectTrigger>
          <SelectContent>
            {field.options?.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case 'number':
      return <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
    case 'date':
      return <Input type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
          <span className="text-sm text-muted-foreground">{field.placeholder}</span>
        </div>
      );
    default:
      return null;
  }
}

CreateTicketPage.displayName = 'CreateTicketPage';
